/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Theme, useTheme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";

import Routes from "src/Routes";

import { useObservable } from "src/framework/store/storeUtil";
import WhatsAppButton from "src/framework/components/buttons/WhatsAppButton";
import { LocalStorage } from "src/framework/commons/LocalStorage";
import { FiltersProvider } from "./FiltersContext";
import { useMediaQuery } from "@mui/material";
import api, { IDataRequest, IDataResponse, STATUS_CODE } from "src/framework/providers/api";
import { PermissionUtil } from "src/framework/commons/PermissionUtil";
import { authUser$ } from "src/framework/components/navigations/MenuUser";
import { ExtraInfoUsuario } from "src/providers/cadastros/ContaProvider/types";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import { getOnlyURLParams } from "src/framework/commons/GetOnlyUrlParams";
import { AssociaDistribuidor } from "./AssociaDistribuidor";
import { openRedirect$ } from "src/App";
import { ModalRedirect } from "./redirect/distribuicao";
import PropostaSimplificada from "./PropostaSimplificada";
import { TermosAtivos } from "./TermosAtivos";
import PropostaDetalhada from "./PropostaDetalhada";
import { ModalCodigoServicoInicial } from "src/components/contas/contas/modalCodigoServicoInicial";
import "./styles.css"
import { useTermosStore } from "src/store/termos/termos.store";
import { LoginStore } from "src/framework/store/LoginStore";
import { ApplicationBar } from "src/framework/components/navigations/ApplicationBar";
import { Menu } from "src/framework/components/navigations/Menu";
import { openMenu$, standAlonePage$, tokenExpirado$ } from "src/framework/store/ApiEvents";
import theme from "src/theme/light";

const drawerWidth = standAlonePage$.getValue() ? 0 : 290;
export const mobileWidth = (window.innerWidth*0.8);

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      backgroundColor: '#fff'
    },
    toolBar: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    drawer: {
      flexShrink: 0,
      width: drawerWidth,
      whiteSpace: 'nowrap',
      background: "linear-gradient(180deg, #F4F4F4 0%, #E2E2E2 100%)",
    },
    drawerOpen: {
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    drawerClose: {
      background: "#E2E2E2",
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflowX: 'hidden',
      width: `calc(${theme.spacing(7)} + 1px)`,
      [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(9)} + 1px)`,
      },
    },
    drawerContainer: {
      overflow: "auto",
      overflowX: 'hidden',
      marginBottom: "40px",
      background: "linear-gradient(180deg, #F4F4F4 0%, #E2E2E2 100%)",
    },
    menuBox: {
      margin: "20px 0px",
      textAlign: "center"
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    hide: {
      display: "none",
    },
    activeLink: {
      color: "inherit",
      textDecoration: "inherit",
    },
    menuItemHeader: theme.mixins.toolbar,
    menuFooter: {
      width: "100%",
      minHeight: "40px",
      background: "#E2E2E2",
      display: "block",
      position: "absolute",
      bottom: "0",
    },
    // menuFooterContent: {
    //   flex: 1,
    // },

    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: openMenu$.getValue() ? `calc(100% - 305px)` : "100hw",
      // marginLeft: drawerWidth,
    },
    contentShift: {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    },
  })
);

interface MainProperties {
  onExit: () => void;
  onInit: () => void;
}

export const MAIN_APP_CONTAINER_ID = "app-main-container"

export const carregarTermos = async (alteraTermosPendentes: (termos: any[]) => void, alteraTermosAceitos: (termos: any[]) => void) =>{
  const requestTermosPendentes: IDataRequest = {
    url: "/termos/usuario/pendentes"
  }
  const responseTermosPendentes: IDataResponse = await api.get(requestTermosPendentes);
  if(responseTermosPendentes.statusCode === STATUS_CODE.OK){
    alteraTermosPendentes(responseTermosPendentes.data)
  }

  const requestTermosAceitos: IDataRequest = {
    url: "/termos/usuario/aceitos"
  }
  const responseTermosAceitos: IDataResponse = await api.get(requestTermosAceitos);
  if(responseTermosAceitos.statusCode === STATUS_CODE.OK){
    alteraTermosAceitos(responseTermosAceitos.data)
  }
}

const Main: FC<MainProperties> =
  ({ onExit = () => { }, onInit = () => { } }) => {
    const classes = useStyles();
    const location = useLocation()
    const history = useHistory()

    const tokenExpirado = useObservable(tokenExpirado$)
    const isLoggedIn = useObservable(LoginStore.isLoggedInSubject())
    const openMenu = useObservable(openMenu$);
    const standAlone = LocalStorage.get("solar.standAlone", true);
    const standAlonePage = useObservable(standAlonePage$)

    const userInfo = LocalStorage.get("solar.userExtraInfo", true);
    const authUser = useObservable<ExtraInfoUsuario | undefined>(authUser$);

    const [userState, setUserState] = useState<any>(null);
    const [isIntelbras, setIsIntelbras] = useState<boolean>(false);

    const permissoes:string[] = PermissionUtil.getPermissions();
    const withPermission = PermissionUtil.can(PermissionUtil.PERMISSION_TELEFONES_LISTAGEM, permissoes);

    const openRedirect = useObservable(openRedirect$);
    const { termosPendentes } = useTermosStore(store => store.state.data);
    const { alteraTermosAceitos , alteraTermosPendentes} = useTermosStore(store => store.actions);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(()=>{
      const query = new URLSearchParams(location.search)
      const code = query.get('code')
      const sessionState = query.get('code')

      if (isLoggedIn && code && sessionState) {
        const queryStr = getOnlyURLParams(location)
        const url = window.location.pathname + (queryStr ? `?${queryStr}` : '')
        history.replace(url)
      }
    },[isLoggedIn, location, history])

    useEffect(() => {
      const userInfo = LocalStorage.get("solar.userExtraInfo", true);
      setIsIntelbras(userInfo?.isIntelbras);
      if (userInfo?.isRevenda) {
        setUserState(userInfo.revenda_id);
      } else if (userInfo?.isDistribuidor) {
        setUserState(userInfo.distribuidor_id);
      } else {
        setUserState(userInfo?.userId);
      }
      onInit();
    }, [isLoggedIn]);

    useEffect(() => {
      if (authUser && userInfo) {
        if (authUser?.isRevenda) {
          setUserState(authUser.revenda_id);
        } else if (authUser?.isDistribuidor) {
          setUserState(authUser.distribuidor_id);
        } else {
          setUserState(userInfo?.userId);
        }
        if (authUser?.isIntelbras) {
          setIsIntelbras(authUser?.isIntelbras);
        }
      }
    }, [userInfo, authUser]);

    useEffect(() => {
      return () => {
        onExit();
      };
    }, []);

    useEffect(() => {
      standAlonePage$.next(standAlone)
    }, [standAlone])
    

    const usuarioIntelbras = (email: string) => {
      if (/@intelbras.com.br\s*$/.test(email)) {
        return true;
      }
      return false;
    }
    
    const propostaSimplificada: boolean = useMemo(() => {
      return location?.pathname?.includes('/propostas/simplificada/')
    }, [location.pathname])

    const propostaDetalhada: boolean = useMemo(() => {
      return location?.pathname?.includes('/propostas/detalhada/')
    }, [location.pathname])


    const verificaDistribuidor = async()=>{
      const userInfo = LocalStorage.get("solar.userExtraInfo", true);
      const usuarioInterno = usuarioIntelbras(userInfo?.email);

      if(userInfo){
        if(location.pathname !== "/signout" && !propostaSimplificada && !propostaDetalhada && !userInfo?.isDistribuidor && !usuarioInterno && !userInfo?.distribuidor_cnpj_cpf){
          history.replace("/associar_distribuidor");
        }
      }
    }
    
    useEffect(()=>{
      if(!propostaSimplificada && !propostaDetalhada && isLoggedIn){
        carregarTermos(alteraTermosPendentes, alteraTermosAceitos);
      }
    },[propostaSimplificada, propostaDetalhada, isLoggedIn])

    useEffect(() => {
      if(termosPendentes?.length === 0 && location.pathname !== '/associar_distribuidor' && !propostaSimplificada && !propostaDetalhada){
        verificaDistribuidor();
      }
    }, [termosPendentes, onExit, onInit])

    const handleClose = () => {
      if (isMobile) openMenu$.next(false);
    };
    return (<>
        {!propostaSimplificada && !propostaDetalhada && termosPendentes?.length > 0 && (
          <TermosAtivos 
            open={true}
            termos={termosPendentes}
            onConfirm={()=>{
              carregarTermos(alteraTermosPendentes, alteraTermosAceitos);
            }}
          />
        )}
        {location.pathname === "/associar_distribuidor" && (
          <Switch>
            <Route exact path={"/associar_distribuidor"} component={AssociaDistribuidor}/>
          </Switch>
        )}

        {propostaSimplificada && (
          <Switch>
            <Route exact path={"/propostas/simplificada/:hash"} component={PropostaSimplificada}/>
          </Switch>
        )}

        {propostaDetalhada && (
          <Switch>
            <Route exact path={"/propostas/detalhada/:hash"} component={PropostaDetalhada}/>
          </Switch>
        )}
        
        {location.pathname !== "/associar_distribuidor" && !propostaSimplificada && !propostaDetalhada && (
          <FiltersProvider>            
          <div className={classes.root}>
            <CssBaseline />
            <ApplicationBar classes={classes} />
            {standAlonePage ? "" : (
              <Drawer
                variant={isMobile ? 'temporary' : 'permanent'}
                anchor="left"
                open={openMenu}
                onClose={handleClose}
                className={clsx(classes.drawer, {
                  [classes.drawerOpen]: openMenu && !isMobile,
                  [classes.drawerClose]: !openMenu && !isMobile,
                })}
                classes={{
                  paper: clsx({
                    [classes.drawerOpen]: openMenu && !isMobile,
                    [classes.drawerClose]: !openMenu && !isMobile,
                  }),
                }}
                ModalProps={{
                  keepMounted: true, // melhora performance no mobile
                }}
                PaperProps={{
                  style: {
                    width: isMobile ? mobileWidth : 'auto',
                    backgroundColor: "#E2E2E2",
                    overflow: "hidden",
                  },
                }}
              >
                {!isMobile && <Toolbar />}
                <Menu classes={classes} />
              </Drawer>
            )}
            <main
              id={MAIN_APP_CONTAINER_ID}
              className={clsx(classes.content, {
                [classes.contentShift]: !openMenu || standAlone,
              })}
            >
              
              <Toolbar />
              <Routes />
              {openRedirect?.status && (
                  <ModalRedirect open={openRedirect?.status}/>
              )}

              {termosPendentes?.length === 0 && !tokenExpirado && authUser && !authUser?.codigo_servico_conta && authUser?.isRevenda && (
                <ModalCodigoServicoInicial />
              )}

            </main>
            {(userState && withPermission) &&(
              <WhatsAppButton 
                userId={userState}
                isIntelbras={isIntelbras}
              />
            )}
          </div>
          </FiltersProvider>
        )}
    </>
    );
  };
export { Main };
